import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { UserPlus, Trash2, Shield, User as UserIcon, Phone, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const { adminAuth } = useOutletContext();
  const navigate = useNavigate();
  const role = localStorage.getItem('adminRole');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: '', phone: '', password: '', role: 'manager' });
  const [saving, setSaving] = useState(false);

  const ROLE_LABELS = {
    super: 'Main Admin', admin: 'Admin (full access)',
    manager: 'Manager', editor: 'Webpage Editor', csr: 'Customer Support',
  };
  const ROLE_DESC = {
    manager: 'Full access (editing + inquiries)',
    editor: 'Website editing sections only',
    csr: 'Inquiries, Visa Inquiries & Saved Itineraries only',
  };

  const headers = { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, { headers: { Authorization: `Basic ${adminAuth}` } });
      if (res.status === 403) { setAdmins(null); return; }
      if (res.ok) setAdmins(await res.json());
    } catch { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  }, [adminAuth]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) { toast.error('Username and password required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, { method: 'POST', headers, body: JSON.stringify(form) });
      if (res.ok) { toast.success('Admin created'); setForm({ username: '', phone: '', password: '', role: 'manager' }); load(); }
      else { const e2 = await res.json().catch(() => ({})); toast.error(e2.detail || 'Failed to create'); }
    } catch { toast.error('Connection error'); }
    finally { setSaving(false); }
  };

  const remove = async (id, username) => {
    if (!window.confirm(`Delete admin "${username}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/admins/${id}`, { method: 'DELETE', headers: { Authorization: `Basic ${adminAuth}` } });
      if (res.ok) { toast.success('Admin deleted'); load(); }
      else { const e2 = await res.json().catch(() => ({})); toast.error(e2.detail || 'Failed to delete'); }
    } catch { toast.error('Connection error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  if (role !== 'super' || admins === null) {
    return (
      <div className="max-w-md mx-auto mt-12 text-center">
        <Shield className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Restricted</h2>
        <p className="text-gray-600 mb-4">Only the main administrator can manage admin users.</p>
        <Button onClick={() => navigate('/admin/dashboard')} variant="outline">Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin Users</h1>
        <p className="text-gray-600">Create and remove back-end users (main admin only).</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add New Admin</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={create} className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
                <div className="relative"><UserIcon className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input className="pl-9" data-testid="new-admin-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="username" /></div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <div className="relative"><Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input className="pl-9" data-testid="new-admin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="optional" /></div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
                <div className="relative"><Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input className="pl-9" type="password" data-testid="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="password" /></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select
                  data-testid="new-admin-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="manager">Manager — full access (editing + inquiries)</option>
                  <option value="editor">Webpage Editor — website editing only</option>
                  <option value="csr">Customer Support — inquiries, visa inquiries & saved itineraries</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">{ROLE_DESC[form.role]}</p>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={saving} data-testid="create-admin-btn" className="bg-amber-600 hover:bg-amber-700">
                  <UserPlus className="w-4 h-4 mr-2" />{saving ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {admins.map((a) => (
          <Card key={a.id} data-testid={`admin-row-${a.id}`} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${a.role === 'super' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                {a.role === 'super' ? <Shield className="w-5 h-5 text-amber-600" /> : <UserIcon className="w-5 h-5 text-gray-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{a.username}</h3>
                  {a.role === 'super'
                    ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Main Admin</span>
                    : <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{ROLE_LABELS[a.role] || 'Admin'}</span>}
                </div>
                {a.phone && <p className="text-xs text-gray-500 mt-0.5">{a.phone}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  Last login: {a.last_login ? new Date(a.last_login).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'}
                </p>
              </div>
              {a.role !== 'super' && (
                <button onClick={() => remove(a.id, a.username)} data-testid={`delete-admin-${a.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0" title="Delete admin">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminUsers;
